import {
  forwardRef,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className = '', ...rest }, ref) {
    return <input ref={ref} className={`input ${className}`.trim()} {...rest} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className = '', ...rest }, ref) {
  return <textarea ref={ref} className={`textarea ${className}`.trim()} {...rest} />;
});

export interface SelectOption {
  value: string | number;
  label: string;
}

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { options: SelectOption[]; placeholder?: string }
>(function Select({ className = '', options, placeholder, ...rest }, ref) {
  return (
    <select ref={ref} className={`select ${className}`.trim()} {...rest}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
});

export interface MultiSelectProps {
  id?: string;
  options: SelectOption[];
  value: number[];
  onChange: (next: number[]) => void;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string | undefined;
}

/** Checkbox list; keyboard friendly and works without JS-heavy widgets. */
export function MultiSelect(props: MultiSelectProps): React.JSX.Element {
  const toggle = (v: number, checked: boolean): void => {
    props.onChange(
      checked ? [...new Set([...props.value, v])] : props.value.filter((x) => x !== v),
    );
  };
  return (
    <div
      className="multiselect"
      role="group"
      id={props.id}
      aria-invalid={props['aria-invalid']}
      aria-describedby={props['aria-describedby']}
    >
      {props.options.length === 0 && <span className="field__label">No options</span>}
      {props.options.map((o) => {
        const v = Number(o.value);
        return (
          <label key={o.value} className="multiselect__item">
            <input
              type="checkbox"
              checked={props.value.includes(v)}
              onChange={(e) => toggle(v, e.target.checked)}
            />
            {o.label}
          </label>
        );
      })}
    </div>
  );
}
